import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import UploadZipFile from "./UploadZipFile";
import ConfigureSpaceImport from "./ConfigureSpaceImport";
import ProcessSpaceImport from "./ProcessSpaceImport";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/ImportDocument.css";
import "../../css/DocumentationHomeComponent.css";

function ImportSpaceDocument({ onComplete, onBack }) {
    const navigate = useNavigate();
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [currentStep, setCurrentStep] = useState('intro'); // 'intro', 'upload', 'configure', 'process', 'completing'
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [completionData, setCompletionData] = useState(null);
    
    
    // ZIP file and extracted data
    const [extractedData, setExtractedData] = useState(null);
    
    // Configuration data
    const [spaceConfig, setSpaceConfig] = useState(null);

    // Context menu options (same as ImportDocument.js)
    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];

    const loggedInOptions = [
        {
            label: "Home",
            icon: <i className="fas fa-home"></i>,
            url: "/",
            onClick: () => navigate("/"),
        },
        {
            label: "Your Documents",
            url: "/documentation",
            onClick: () => navigate("/documentation"),
        },
        {
            label: "Your Walkthroughs",
            url: "/walkthroughs",
            onClick: () => navigate("/walkthroughs"),
        },
        {
            label: "Your Notebook",
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Your Workspace",
            url: "/workspaces",
            onClick: () => navigate("/workspaces"),
        },
        {
            label: "Account Settings",
            url: "/account", // Will be updated when we have user context
            onClick: () => navigate("/account"),
        },
    ];

    const handleArrowClick = () => {
        setIsTransitioning(true);
        // Small delay to allow transition to start
        setTimeout(() => {
            setCurrentStep('upload');
            setIsTransitioning(false);
        }, 150);
    };

    const handleZipUploaded = (data) => {
        setExtractedData(data);
        setCurrentStep('configure');
    };

    const handleConfigurationComplete = (config) => {
        setSpaceConfig(config);
        
        // Check if the import was already completed in ConfigureSpaceImport
        if (config.importResult) {
            // Import is already complete, go directly to completion
            // Pass the space name and user UID from the config since spaceConfig state might not be updated yet
            handleProcessComplete(null, config.importResult.pages, config.spaceName, config.authorUid);
        } else {
            // Import not yet complete, go to processing step
            setCurrentStep('process');
        }
    };

    const handleBackToIntro = () => {
        setCurrentStep('intro');
        setExtractedData(null);
        setSpaceConfig(null);
        setIsTransitioning(false);
    };

    const handleBackToUpload = () => {
        setCurrentStep('upload');
        setSpaceConfig(null);
    };

    const handleBackToConfigure = () => {
        setCurrentStep('configure');
    };

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    const handleProcessComplete = (spaceId, pages, spaceName = null, authorUid = null) => {
        setCompletionData({ spaceId, pages });
        setCurrentStep('completing');
        
        // Use the provided spaceName or fall back to spaceConfig.spaceName
        const nameToUse = spaceName || spaceConfig?.spaceName;
        // Use the provided authorUid or fall back to spaceConfig.authorUid
        const uidToUse = authorUid || spaceConfig?.authorUid;
        
        // Show success message briefly, then redirect to the newly created space
        // Note: The URL pattern for spaces is /documentation/space/${spaceName}/${userUid}/
        setTimeout(() => {
            console.log('Redirecting to newly created space after successful import');
            console.log('Space name:', nameToUse, 'Author UID:', uidToUse);
            navigate(`/documentation/space/${nameToUse}/${uidToUse}/`);
        }, 2000); // 2 second delay to show completion message
    };

    // Check if user is signed in
    const { auth } = require('../../firebase');
    const currentUser = auth.currentUser;
    
    // Check if user is signed in for menu options
    const menuOptions = currentUser ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);
    
    if (!currentUser) {
        return (
            <div 
                className="DocumentationMainPage__div"
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
            >
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>To import spaces, you must have an account with us</strong>. If you already have an account, 
                        you can login by clicking the Login button below. If you don't already have an account, you can create a new 
                        one by clicking the Register button below.
                    </p>
                    <button onClick={() => navigate('/')} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                    <button onClick={() => navigate('/login')} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                    <button onClick={() => navigate('/register')} className="ConflictResolver__button">Register</button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    const renderStep = () => {
        switch (currentStep) {
            case 'intro':
                return (
                    <>
                        <div className="ImportDocument__content">
                            <h2>Import Confluence Space</h2>

                            <p className="medium" style={{ marginTop: "20px" }}>
                                Thank you for choosing Bluebird Documentation! This process allows you to import 
                                an entire Confluence space with all its pages and hierarchy.
                            </p>

                            <p className="small">
                                We'll try to make instructions as clear as possible. If you're facing issues, need further guidance, 
                                or have encountered a bug, please feel free to submit a support ticket.
                            </p>

                            <p className="medium semi-bold">
                                Below is a general overview of the process.
                            </p>

                            <ol>
                                <li>Click the arrow on the right of the page to begin.</li>
                                <li>Upload the ZIP file you exported from Confluence.</li>
                                <li>Configure your space import settings (name, which pages to include, etc.).</li>
                                <li>We'll process the import and create your space with all pages and hierarchy.</li>
                                <li>Review and access your newly imported space!</li>
                            </ol>

                            <p className="small">
                                This feature imports entire Confluence spaces while preserving page relationships and structure. 
                                We really appreciate you trying this feature, and please feel free to submit feedback so we can continue to improve it!
                            </p>
                        </div>

                        <div 
                            className="ImportDocument__arrow" 
                            onClick={handleArrowClick}
                            title="Click to continue"
                            aria-label="Continue to next step"
                        >
                            ❯
                        </div>
                    </>
                );
            case 'upload':
                return (
                    <UploadZipFile 
                        onNext={handleZipUploaded}
                        onBack={handleBackToIntro}
                        enhancedRightClickEnabled={enhancedRightClickEnabled}
                        showContextMenu={showContextMenu}
                        ContextMenuComponent={ContextMenuComponent}
                    />
                );
            case 'configure':
                return (
                    <ConfigureSpaceImport 
                        extractedData={extractedData}
                        onNext={handleConfigurationComplete}
                        onBack={handleBackToUpload}
                        enhancedRightClickEnabled={enhancedRightClickEnabled}
                        showContextMenu={showContextMenu}
                        ContextMenuComponent={ContextMenuComponent}
                    />
                );
            case 'process':
                return (
                    <ProcessSpaceImport 
                        extractedData={extractedData}
                        spaceConfig={spaceConfig}
                        onComplete={handleProcessComplete}
                        onBack={handleBackToConfigure}
                        enhancedRightClickEnabled={enhancedRightClickEnabled}
                        showContextMenu={showContextMenu}
                        ContextMenuComponent={ContextMenuComponent}
                    />
                );
            case 'completing':
                return (
                    <div 
                        className="ImportDocument__main-div"
                        onContextMenu={
                            enhancedRightClickEnabled
                            ? (e) => {
                                e.preventDefault();
                                showContextMenu(e.clientX, e.clientY);
                                }
                            : undefined
                        }
                    >
                        <div className="ImportDocument__content">
                            <p>Redirecting to your newly created space...</p>
                        </div>
                        {enhancedRightClickEnabled && ContextMenuComponent}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div 
            className={`ImportDocument__main-div ${currentStep === 'intro' && isTransitioning ? 'transitioning' : isTransitioning ? 'transitioning-back' : ''}`}
            onContextMenu={
                currentStep === 'intro' && enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            {renderStep()}
            {currentStep === 'intro' && enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default ImportSpaceDocument;
